import { Link, useLocation, useNavigate } from 'react-router-dom';
import { memo, useEffect, useState } from 'react';
import { Search, X, ListChecks, User, Settings } from 'lucide-react';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import { openModal, setMobileMenuOpen, setSidebarHoverExpanded } from '@store/slices/uiSlice';
import { cn } from '@utils/cn';
import logoBlack from '@assets/drahft_logo.webp';
import logoCollapsed from '@assets/drahft_collapse.png';
import { APP_ROUTES } from '@config/routes';
import { clientsNav, mainNav, shortcutsNav, supportNavItem } from './dashboardNavigation';
import type { OnboardingStatus } from '@/hooks/useOnboardingStatus';

const BOTTOM_ACCOUNT_LINKS = new Set(['Settings']);

interface SidebarProps {
  onboardingStatus: OnboardingStatus;
}

const Sidebar = ({ onboardingStatus }: SidebarProps) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { sidebarOpen, sidebarHoverExpanded, mobileMenuOpen } = useAppSelector(state => state.ui);
  const { user, userProfile } = useAppSelector(state => state.auth);
  const showSetupChecklist = !onboardingStatus.isLoading;
  const [failedAvatarUrls, setFailedAvatarUrls] = useState<string[]>([]);
  const avatarUrl = [userProfile?.photoURL, user?.photoURL].find(
    url => url && !failedAvatarUrls.includes(url)
  );
  const isSidebarExpanded = sidebarOpen || mobileMenuOpen || sidebarHoverExpanded;
  const navLabelClass = cn(
    'overflow-hidden whitespace-nowrap transition-[width,opacity] duration-300 ease-in-out',
    isSidebarExpanded ? 'ml-3 max-w-40 opacity-100' : 'ml-0 max-w-0 opacity-0'
  );

  const visibleShortcutsNav = shortcutsNav.filter(item => !BOTTOM_ACCOUNT_LINKS.has(item.name));
  const settingsNavItem = shortcutsNav.find(item => item.name === 'Settings');
  const settingsHref = settingsNavItem?.href ?? APP_ROUTES.SETTINGS;

  useEffect(() => {
    setFailedAvatarUrls([]);
  }, [user?.uid, user?.photoURL, userProfile?.photoURL]);

  useEffect(() => {
    if (sidebarOpen || mobileMenuOpen) {
      dispatch(setSidebarHoverExpanded(false));
    }
  }, [dispatch, sidebarOpen, mobileMenuOpen]);

  const handleLinkClick = () => {
    dispatch(setMobileMenuOpen(false));
  };

  const openGlobalSearch = () => {
    dispatch(setMobileMenuOpen(false));
    dispatch(openModal({ id: 'global.search' }));
  };

  const openSetupChecklist = () => {
    dispatch(setMobileMenuOpen(false));
    dispatch(openModal({ id: 'onboarding.checklist' }));
  };

  const formatPlan = (plan?: string | null) => {
    if (!plan) return '';
    const normalizedPlan = plan.replace(/^pending_/, '');
    const planNames: Record<string, string> = {
      free: 'Free Plan',
      hobby: 'Hobby Plan',
      basic: 'Essentials Plan',
      professional: 'Professional Plan',
      enterprise: 'Enterprise Plan',
      custom: 'Custom Plan',
    };

    if (plan.includes(' ')) return plan;
    return (
      planNames[normalizedPlan] ||
      `${normalizedPlan.charAt(0).toUpperCase()}${normalizedPlan.slice(1)}`
    );
  };

  const handleAccountNavigate = (route: string) => {
    dispatch(setMobileMenuOpen(false));
    navigate(route);
  };

  const isActiveRoute = (href: string) => {
    return location.pathname === href;
  };

  return (
    <>
      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div
          className="fixed inset-0 z-40 bg-night/50 lg:hidden"
          role="button"
          tabIndex={0}
          aria-label="Close menu"
          onClick={() => dispatch(setMobileMenuOpen(false))}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar' || e.key === 'Escape') {
              dispatch(setMobileMenuOpen(false));
            }
          }}
        />
      )}

      {/* Unified Sidebar - Mobile & Desktop */}
      <aside
        onMouseEnter={() => {
          if (!mobileMenuOpen && !sidebarOpen) {
            dispatch(setSidebarHoverExpanded(true));
          }
        }}
        onMouseLeave={() => {
          if (sidebarHoverExpanded) {
            dispatch(setSidebarHoverExpanded(false));
          }
        }}
        className={cn(
          'fixed left-0 top-0 h-screen bg-neutral-light border-r border-neutral-200 shadow-lg transition-[width] duration-500 z-50 font-sans',
          mobileMenuOpen ? 'translate-x-0 w-full lg:w-56' : '-translate-x-full lg:translate-x-0',
          // Desktop styles
          isSidebarExpanded ? 'lg:w-56' : 'lg:w-20'
        )}
      >
        <div className="flex flex-col h-full shadow-lg">
          {/* Header */}
          <div className="relative flex h-[72px] shrink-0 items-center justify-center px-4">
            <div className="flex items-center justify-center flex-shrink-0">
              {isSidebarExpanded ? (
                <img src={logoBlack} alt="drahft" className="object-contain w-auto h-10" />
              ) : (
                <img
                  src={logoCollapsed}
                  alt="drahft compact"
                  className="object-contain w-11 h-11"
                />
              )}
            </div>

            {/* Close button for mobile */}
            <button
              type="button"
              aria-label="Close menu"
              onClick={() => dispatch(setMobileMenuOpen(false))}
              className="absolute p-2 transition-colors -translate-y-1/2 rounded-lg right-4 top-1/2 btn-compact lg:hidden hover:bg-primary-150"
            >
              <X size={24} className="text-neutral-dark" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 overflow-y-auto">
            {/* Main Section */}
            <div>
              <ul className="px-0">
                <li>
                  <button
                    type="button"
                    onClick={openGlobalSearch}
                    className="flex items-center justify-start w-full px-3 py-1 text-base transition-colors rounded-lg btn-compact text-text-main hover:bg-primary-150"
                  >
                    <Search size={18} className="flex-shrink-0" />
                    <span className={navLabelClass}>Search</span>
                  </button>
                </li>
                {mainNav.map(item => {
                  const isActive = isActiveRoute(item.href);
                  return (
                    <li key={item.name}>
                      <Link
                        to={item.href}
                        onClick={handleLinkClick}
                        className={cn(
                          'flex items-center justify-start px-3 py-1 rounded-lg transition-colors text-base',
                          isActive
                            ? 'bg-primary-100 text-primary-700 font-medium'
                            : 'text-text-main hover:bg-primary-150'
                        )}
                      >
                        <item.icon size={18} className="flex-shrink-0" />
                        <span className={navLabelClass}>{item.name}</span>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>

            {/* Clients Section */}
            <div className="pt-4 mt-4 border-t border-gray-200">
              <ul className="px-0">
                {clientsNav.map(item => {
                  const isActive = isActiveRoute(item.href);
                  return (
                    <li key={item.name}>
                      <Link
                        to={item.href}
                        onClick={handleLinkClick}
                        className={cn(
                          'flex items-center justify-start px-3 py-1 rounded-lg transition-colors text-base',
                          isActive
                            ? 'bg-primary-100 text-primary-700 font-medium'
                            : 'text-text-main hover:bg-primary-150'
                        )}
                      >
                        <item.icon size={18} className="flex-shrink-0" />
                        <span className={navLabelClass}>{item.name}</span>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>

            {/* Shortcuts Section */}
            <div className="pt-4 mt-4 border-t border-gray-200">
              <ul className="px-0">
                {visibleShortcutsNav.map(item => {
                  const isActive = isActiveRoute(item.href);
                  return (
                    <li key={item.name}>
                      <Link
                        to={item.href}
                        onClick={handleLinkClick}
                        className={cn(
                          'flex items-center justify-start px-3 py-1 rounded-lg transition-colors text-base',
                          isActive
                            ? 'bg-primary-100 text-primary-700 font-medium'
                            : 'text-text-main hover:bg-primary-150'
                        )}
                      >
                        <item.icon size={18} className="flex-shrink-0" />
                        <span className={navLabelClass}>{item.name}</span>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>
          </nav>

          <div className="p-4">
            {showSetupChecklist ? (
              <button
                type="button"
                onClick={openSetupChecklist}
                aria-label="Open setup checklist"
                title="Open setup checklist"
                className={cn(
                  'mb-2 flex w-full items-center rounded-lg border border-amber-300 bg-amber-50 px-3 py-1 text-sm font-semibold text-amber-800 shadow-sm transition-colors hover:bg-amber-100'
                )}
              >
                <ListChecks size={16} className="shrink-0" />
                <span className={navLabelClass}>Setup Checklist</span>
              </button>
            ) : null}

            <Link
              to={supportNavItem.href}
              onClick={handleLinkClick}
              className={cn(
                'mb-2 flex w-full items-center rounded-lg px-3 py-1 text-base transition-colors',
                isActiveRoute(supportNavItem.href)
                  ? 'bg-primary-100 font-medium text-primary-700'
                  : 'text-text-main hover:bg-primary-150'
              )}
            >
              <supportNavItem.icon size={18} className="flex-shrink-0" />
              <span className={navLabelClass}>{supportNavItem.name}</span>
            </Link>

            <button
              type="button"
              onClick={() => handleAccountNavigate(settingsHref)}
              aria-label="Open account settings"
              className={cn(
                'non-global-button flex h-11 w-full shrink-0 items-center rounded-lg border border-gray-200 bg-white px-3 shadow-sm transition-[width,padding,background-color] duration-300 ease-in-out hover:bg-gray-50',
                !isSidebarExpanded &&
                  'lg:justify-center lg:px-0 lg:border-transparent lg:bg-transparent lg:shadow-none lg:hover:bg-transparent'
              )}
            >
              {avatarUrl ? (
                <img
                  key={avatarUrl}
                  src={avatarUrl}
                  alt={userProfile?.displayName || user?.displayName || 'Profile'}
                  referrerPolicy="no-referrer"
                  className="object-cover w-8 h-8 rounded-full shrink-0"
                  onError={() => setFailedAvatarUrls(urls => [...urls, avatarUrl])}
                />
              ) : (
                <div className="flex items-center justify-center w-8 h-8 rounded-full shrink-0 bg-primary-100">
                  <User size={16} className="text-primary-600" />
                </div>
              )}
              <div
                className={cn(
                  'min-w-0 overflow-hidden whitespace-nowrap transition-[width,opacity] duration-300 ease-in-out',
                  isSidebarExpanded ? 'ml-3 max-w-40 opacity-100' : 'ml-0 max-w-0 opacity-0'
                )}
              >
                <p className="text-sm font-medium text-left text-gray-900 truncate">
                  {userProfile?.displayName || 'Account'}
                </p>
                <p className="text-left text-[11px] text-gray-500 truncate">
                  {userProfile?.subscriptionPlan
                    ? formatPlan(userProfile.subscriptionPlan)
                    : userProfile?.role || 'Workspace'}
                </p>
              </div>
              <span
                className={cn(
                  'ml-auto flex h-8 shrink-0 items-center justify-center overflow-hidden rounded-lg text-gray-600 transition-[width,opacity] duration-300 ease-in-out',
                  isSidebarExpanded ? 'w-8 opacity-100' : 'ml-0 w-0 opacity-0 pointer-events-none'
                )}
                aria-hidden="true"
              >
                <Settings
                  size={16}
                  className={cn(
                    'transition-transform duration-1000 ease-in-out',
                    isSidebarExpanded && 'rotate-180'
                  )}
                />
              </span>
            </button>
          </div>
        </div>
      </aside>
    </>
  );
};

// Memo-ize to prevent unnecessary re-renders on navigation
export default memo(Sidebar);
